import { useState, useEffect, useRef } from 'react'
import { X, Search, Plus, Check, Tag } from 'lucide-react'
import { Badge } from '@univdiam/ui/components/badge'
import { labelStyle } from '@univdiam/ui/lib/tagColors'

export interface AdminTag {
  id: number
  name: string
  color: string
}

type AddLabelsProps = {
  orderId: string
  orderRef: string
  customerId: string
  labels: string[]
  adminTags?: AdminTag[]
  fetchTags?: (
    page: number,
    limit: number,
    search: string
  ) => Promise<{ tags: AdminTag[]; totalPages: number }>
  onUpdateOrder?: (
    order_id: string,
    customer_id: string,
    updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string }
  ) => void
}

function AddLabels({
  orderId,
  orderRef,
  customerId,
  labels,
  adminTags = [],
  fetchTags,
  onUpdateOrder,
}: AddLabelsProps) {
  const [inputValue, setInputValue] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [localLabels, setLocalLabels] = useState(labels)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Pagination states for dynamic fetch on scroll
  const [tags, setTags] = useState<AdminTag[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    setLocalLabels(labels)
  }, [labels])


  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Reset and load first page of tags when search changes or when dropdown opens
  useEffect(() => {
    if (!fetchTags || !dropdownOpen) return

    let isMounted = true
    setLoading(true)
    setPage(1)
    setHasMore(true)

    fetchTags(1, 10, debouncedSearch)
      .then(res => {
        if (!isMounted) return
        setTags(res.tags)
        setHasMore(res.tags.length > 0 && 1 < res.totalPages)
        setPage(2)
      })
      .catch(err => {
        console.error('Error fetching tags:', err)
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [debouncedSearch, fetchTags, dropdownOpen])

  // Load more tags when user scrolls to bottom
  const loadMoreTags = async () => {
    if (!fetchTags || loading || !hasMore) return
    setLoading(true)

    try {
      const res = await fetchTags(page, 10, debouncedSearch)
      if (res.tags.length > 0) {
        setTags(prev => [...prev, ...res.tags])
        setPage(prev => prev + 1)
        setHasMore(page < res.totalPages)
      } else {
        setHasMore(false)
      }
    } catch (err) {
      console.error('Error loading more tags:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 15) {
      loadMoreTags()
    }
  }

  const saveLabels = (updated: string[]) => {
    setLocalLabels(updated)
    onUpdateOrder?.(orderId, customerId, { labels: updated, orderRef })
  }

  const addLabel = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed || trimmed.length > 50) return
    if (localLabels.some(label => label.trim().toLowerCase() === trimmed.toLowerCase())) return
    saveLabels([...localLabels, trimmed])
    setInputValue('')
    inputRef.current?.focus()
  }

  const removeLabel = (index: number) => {
    saveLabels(localLabels.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (inputValue.trim()) {
        addLabel(inputValue)
        setDropdownOpen(true)
      }
    } else if (e.key === 'Escape') {
      setDropdownOpen(false)
    }
  }

  const displayedTags = fetchTags ? tags : adminTags

  // Admin tags filtered by search text, excluding already applied
  const filteredAdminTags = displayedTags.filter(t => {
    const notApplied = !localLabels.some(
      label => label.trim().toLowerCase() === t.name.trim().toLowerCase()
    )
    const matchesSearch = fetchTags
      ? true // Already filtered on the backend
      : !inputValue.trim() || t.name.toLowerCase().includes(inputValue.toLowerCase())
    return notApplied && matchesSearch
  })

  // Show "Add custom" when typed text doesn't exactly match an admin tag
  const showAddCustom =
    inputValue.trim().length > 0 &&
    inputValue.trim().length <= 50 &&
    !displayedTags.some(t => t.name.toLowerCase() === inputValue.trim().toLowerCase()) &&
    !localLabels.some(label => label.trim().toLowerCase() === inputValue.trim().toLowerCase())

  const showDropdown = dropdownOpen && (filteredAdminTags.length > 0 || showAddCustom)

  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-lg shadow-lg w-[240px]"
      onClick={e => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#F0F0F0]">
        <span className="w-[3px] h-4 bg-[#883734] rounded-full shrink-0" />
        <span className="text-sm font-semibold text-gray-800">Add Labels</span>
      </div>

      {/* ── Combobox input ── */}
      <div ref={containerRef} className="p-2.5 relative">
        <div
          className={`flex items-center gap-2 h-8 px-2.5 rounded-md border transition-all cursor-text
            ${dropdownOpen
              ? 'border-[#883734] ring-1 ring-[#883734]/20 bg-white'
              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
            }`}
          onClick={() => { inputRef.current?.focus(); setDropdownOpen(true) }}
        >
          <Search size={11} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder={displayedTags.length > 0 ? 'Search or type a label...' : 'Type a label...'}
            value={inputValue}
            maxLength={50}
            onChange={e => {
              setInputValue(e.target.value)
              setDropdownOpen(true)
            }}
            onFocus={() => setDropdownOpen(true)}
            onKeyDown={handleKeyDown}
            className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder:text-gray-400 min-w-0"
          />
          {inputValue && (
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); setInputValue(''); inputRef.current?.focus() }}
              className="text-gray-400 hover:text-gray-600 shrink-0"
            >
              <X size={10} />
            </button>
          )}
        </div>

        {/* ── Dropdown list ── */}
        {showDropdown && (
          <div
            onScroll={handleScroll}
            className="absolute left-2.5 right-2.5 top-[calc(100%-4px)] bg-white border border-[#E5E5E5] rounded-md shadow-md z-[200] max-h-[200px] overflow-y-auto"
          >
            {/* Admin tag options */}
            {filteredAdminTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                onMouseDown={e => {
                  e.preventDefault()
                  addLabel(tag.name)
                  setDropdownOpen(filteredAdminTags.length > 1) // keep open if more tags
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#883734]/5 text-left transition-colors group"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0 border border-black/10"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1 text-xs text-gray-700 group-hover:text-gray-900">{tag.name}</span>
                <Plus size={11} className="text-gray-300 group-hover:text-[#883734] shrink-0 transition-colors" />
              </button>
            ))}

            {/* Separator */}
            {filteredAdminTags.length > 0 && showAddCustom && (
              <div className="h-px bg-gray-100 mx-3" />
            )}

            {/* Add custom label option */}
            {showAddCustom && (
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault()
                  addLabel(inputValue)
                  setDropdownOpen(false)
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#883734]/5 text-left transition-colors group"
              >
                <Tag size={11} className="text-[#883734] shrink-0" />
                <span className="text-xs text-gray-600 group-hover:text-gray-900">
                  Add <span className="font-semibold text-[#883734]">"{inputValue.trim()}"</span>
                </span>
              </button>
            )}

            {loading && (
              <div className="text-center py-2 text-[10px] text-gray-400 border-t border-gray-50">
                Loading...
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Already applied hint when nothing to show ── */}
      {dropdownOpen && inputValue.trim() && filteredAdminTags.length === 0 && !showAddCustom && (
        <p className="px-3 pb-2 text-[10px] text-gray-400">
          Label already applied.
        </p>
      )}

      {/* ── Applied labels ── */}
      {localLabels.length > 0 && (
        <div className="px-3 pb-3 pt-1 border-t border-[#F0F0F0]">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 pt-1">
            Applied
          </p>
          <div className="flex flex-wrap gap-1">
            {localLabels.map((label, index) => {
              return (
                <Badge
                  key={index}
                  variant="outline"
                  className="px-1.5 py-0.5 text-[11px] rounded flex items-center gap-1 cursor-default font-normal"
                  style={labelStyle(label, adminTags.length ? adminTags : tags)}
                >
                  {label}
                  <button
                    type="button"
                    className="cursor-pointer hover:text-red-500 transition-colors inline-flex items-center ml-0.5"
                    onClick={e => {
                      e.stopPropagation()
                      removeLabel(index)
                    }}
                  >
                    <X size={10} strokeWidth={2} />
                  </button>
                </Badge>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default AddLabels
